import React, { useState } from "react";
import api, { API_BASE } from "../../../utils/api";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { API_ENDPOINTS, VALIDATION } from "../constants";
import { getAppConfig } from "../../../utils/appConfig";

/**
 * Product Search Component
 * Search and select products to add to order with autocomplete dropdown
 * Searches when 2+ characters are entered
 *
 * @param {Object} props
 * @param {Function} props.onSelect - Callback function when product is selected
 */
const ProductSearch = ({ onSelect }) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const { defaultImage } = getAppConfig();

  const getImageSrc = (product) => {
    const path = product.image || product.imageUrl;

    if (path) {
      if (path.startsWith("http")) return path;
      return `${API_BASE}${path}`;
    }

    if (defaultImage) {
      return `${API_BASE}${defaultImage}`;
    }

    return "/img/finglider_logo.jpg";
  };
  const handleSearch = async (value) => {
    setInputValue(value);

    if (value.length < VALIDATION.MIN_SEARCH_LENGTH) {
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(
        `${API_ENDPOINTS.SEARCH_PRODUCTS}?query=${value}`,
      );
      setOptions(res.data);
      setShowDropdown(res.data.length > 0);
    } catch (e) {
      console.log("Search error", e);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    onSelect({
      ...product,
      price: product.price,
      offerPrice: product.offerPrice,
      finalPrice: product.offerPrice || product.price,
      imageUrl: getImageSrc(product),
    });
    setInputValue("");
    setOptions([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => options.length > 0 && setShowDropdown(true)}
          placeholder="Search products by name..."
          className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <svg
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute z-40 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No products found
              </div>
            ) : (
              options.map((product) => (
                <div
                  key={product._id}
                  onClick={() => handleSelectProduct(product)}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  {/* Product Image */}
                  <div className="w-12 h-12 flex-shrink-0">
                    <img
                      src={getImageSrc(product)}
                      alt={product.productName}
                      className="w-full h-full object-cover rounded"
                      onError={(e) => {
                        e.target.src = defaultImage
                          ? `${API_BASE}${defaultImage}`
                          : "/img/finglider_logo.jpg";
                      }}
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {product.productName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {product.categoryName || "Uncategorized"}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    {product.offerPrice ? (
                      <>
                        <div className="text-sm font-semibold text-green-600">
                          {currencySymbol}
                          {product.offerPrice}
                        </div>
                        <div className="text-xs text-gray-400 line-through">
                          {currencySymbol}
                          {product.price}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm font-semibold text-gray-900">
                        {currencySymbol}
                        {product.price}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSearch;
